/**
 * PortalRPUS — Revenue Per User Session Dashboard
 *
 * THE differentiating metric. Shows:
 * - Overall RPUS across all traffic
 * - RPUS by vertical, by source, by affiliate
 * - Consumer LTV tiers (new/low/medium/high/whale)
 * - Returning vs new user performance
 * - Top consumers by lifetime revenue
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { DollarSign, Users, TrendingUp, Loader2, Crown, Repeat, UserPlus } from 'lucide-react';
import { supabaseGet } from '@/utils/supabaseRest';

interface Consumer {
  id: string;
  email: string | null;
  phone: string | null;
  total_sessions: number;
  total_conversions: number;
  total_revenue: number;
  rpus: number;
  ltv_tier: string;
  converted_verticals: string[];
  first_seen_at: string;
  last_seen_at: string;
  first_affiliate_id: string | null;
  first_utm_source: string | null;
  avg_intent_score: number;
  avg_monetization_score: number;
}

interface ScoringResult {
  session_id: string;
  intent_score: number;
  monetization_score: number;
  decision: string;
  vertical_id: string | null;
}

interface Outcome {
  session_id: string;
  revenue: number;
  outcome_status: string;
  vertical_id: string | null;
}

const TIER_COLORS: Record<string, string> = {
  whale: '#f59e0b', high: '#10b981', medium: '#3b82f6', low: '#8b5cf6', new: '#64748b',
};

const TIER_LABELS: Record<string, string> = {
  whale: 'Whale ($500+)', high: 'High ($200+)', medium: 'Medium ($50+)', low: 'Low ($1+)', new: 'New ($0)',
};

export default function PortalRPUS() {
  const [consumers, setConsumers] = useState<Consumer[]>([]);
  const [results, setResults] = useState<ScoringResult[]>([]);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, o] = await Promise.all([
        supabaseGet('consumer_profiles?order=total_revenue.desc&limit=500'),
        supabaseGet('scoring_results?select=session_id,intent_score,monetization_score,decision,vertical_id&limit=1000'),
        supabaseGet('outcomes?select=session_id,revenue,outcome_status,vertical_id&limit=1000'),
      ]);
      setConsumers(c || []);
      setResults(r || []);
      setOutcomes(o || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── KPIs ──
  const totalSessions = results.length;
  const totalRevenue = outcomes.filter(o => o.outcome_status === 'converted').reduce((s, o) => s + Number(o.revenue || 0), 0);
  const overallRPUS = totalSessions > 0 ? totalRevenue / totalSessions : 0;
  const totalConsumers = consumers.length;
  const returningConsumers = consumers.filter(c => c.total_sessions > 1).length;
  const returningRate = totalConsumers > 0 ? (returningConsumers / totalConsumers * 100) : 0;
  const avgLTV = totalConsumers > 0 ? totalRevenue / totalConsumers : 0;

  // ── LTV Tier distribution ──
  const tierCounts = useMemo(() => {
    const counts: Record<string, number> = { whale: 0, high: 0, medium: 0, low: 0, new: 0 };
    consumers.forEach(c => { counts[c.ltv_tier || 'new'] = (counts[c.ltv_tier || 'new'] || 0) + 1; });
    return Object.entries(counts).map(([tier, count]) => ({ name: TIER_LABELS[tier] || tier, value: count, tier }));
  }, [consumers]);

  // ── RPUS by vertical ──
  const rpusByVertical = useMemo(() => {
    const map: Record<string, { sessions: number; revenue: number }> = {};
    const outcomeMap = new Map(outcomes.map(o => [o.session_id, o]));
    results.forEach(r => {
      const v = r.vertical_id || 'unknown';
      if (!map[v]) map[v] = { sessions: 0, revenue: 0 };
      map[v].sessions++;
      const o = outcomeMap.get(r.session_id);
      if (o?.outcome_status === 'converted') map[v].revenue += Number(o.revenue || 0);
    });
    return Object.entries(map)
      .map(([vertical, d]) => ({ vertical: vertical.replace(/_/g, ' '), rpus: d.sessions > 0 ? Math.round(d.revenue / d.sessions * 100) / 100 : 0, sessions: d.sessions, revenue: Math.round(d.revenue * 100) / 100 }))
      .sort((a, b) => b.rpus - a.rpus)
      .slice(0, 10);
  }, [results, outcomes]);

  // ── Returning vs New performance ──
  const returningVsNew = useMemo(() => {
    const returning = consumers.filter(c => c.total_sessions > 1);
    const newUsers = consumers.filter(c => c.total_sessions === 1);
    const retRev = returning.reduce((s, c) => s + Number(c.total_revenue || 0), 0);
    const newRev = newUsers.reduce((s, c) => s + Number(c.total_revenue || 0), 0);
    const retSessions = returning.reduce((s, c) => s + (c.total_sessions || 0), 0);
    const newSessions = newUsers.reduce((s, c) => s + (c.total_sessions || 0), 0);
    return [
      { type: 'Returning', users: returning.length, sessions: retSessions, revenue: Math.round(retRev * 100) / 100, rpus: retSessions > 0 ? Math.round(retRev / retSessions * 100) / 100 : 0 },
      { type: 'New', users: newUsers.length, sessions: newSessions, revenue: Math.round(newRev * 100) / 100, rpus: newSessions > 0 ? Math.round(newRev / newSessions * 100) / 100 : 0 },
    ];
  }, [consumers]);

  return (
    <PortalLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-emerald-400" />
            Revenue Per User Session (RPUS)
          </h1>
          <p className="text-sm text-slate-400 mt-1">The metric that matters. How much revenue does each user session generate across their lifetime?</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 text-emerald-400 animate-spin" /></div>
        ) : (
          <>
            {/* Hero RPUS */}
            <Card className="bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border-emerald-500/30">
              <CardContent className="p-8 text-center">
                <div className="text-xs text-emerald-400 uppercase tracking-wider mb-2">Revenue Per User Session</div>
                <div className="text-5xl font-bold text-white">${overallRPUS.toFixed(2)}</div>
                <div className="text-sm text-slate-400 mt-2">{totalSessions.toLocaleString()} sessions • ${totalRevenue.toFixed(2)} total revenue</div>
              </CardContent>
            </Card>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KpiCard label="Total Consumers" value={totalConsumers.toLocaleString()} icon={<Users className="h-5 w-5" />} color="text-blue-400" />
              <KpiCard label="Returning Users" value={`${returningRate.toFixed(1)}%`} icon={<Repeat className="h-5 w-5" />} color="text-violet-400" />
              <KpiCard label="New Users" value={`${(100 - returningRate).toFixed(1)}%`} icon={<UserPlus className="h-5 w-5" />} color="text-slate-400" />
              <KpiCard label="Avg LTV" value={`$${avgLTV.toFixed(2)}`} icon={<TrendingUp className="h-5 w-5" />} color="text-amber-400" />
              <KpiCard label="Top Consumer" value={consumers[0] ? `$${Number(consumers[0].total_revenue || 0).toFixed(2)}` : '$0'} icon={<Crown className="h-5 w-5" />} color="text-yellow-400" />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* RPUS by Vertical */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">RPUS by Vertical</CardTitle></CardHeader>
                <CardContent>
                  {rpusByVertical.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={rpusByVertical} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                        <YAxis type="category" dataKey="vertical" tick={{ fill: '#94a3b8', fontSize: 11 }} width={110} />
                        <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8 }} />
                        <Bar dataKey="rpus" name="RPUS ($)" fill="#10b981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-sm text-slate-500">No data yet — RPUS will appear as conversions flow through</div>
                  )}
                </CardContent>
              </Card>

              {/* LTV Tier Distribution */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Consumer LTV Tiers</CardTitle></CardHeader>
                <CardContent>
                  {tierCounts.some(t => t.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={tierCounts} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {tierCounts.map((entry) => (
                            <Cell key={entry.tier} fill={TIER_COLORS[entry.tier] || '#6b7280'} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-sm text-slate-500">No consumer profiles yet</div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Returning vs New */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Returning vs New User Performance</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-slate-500 border-b border-slate-700">
                      <th className="text-left py-2">Type</th>
                      <th className="text-right py-2">Users</th>
                      <th className="text-right py-2">Sessions</th>
                      <th className="text-right py-2">Revenue</th>
                      <th className="text-right py-2">RPUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returningVsNew.map(r => (
                      <tr key={r.type} className="border-b border-slate-800">
                        <td className="py-2 flex items-center gap-2">
                          {r.type === 'Returning' ? <Repeat className="h-4 w-4 text-violet-400" /> : <UserPlus className="h-4 w-4 text-slate-400" />}
                          <span className="text-white font-medium">{r.type}</span>
                        </td>
                        <td className="py-2 text-right text-slate-300">{r.users}</td>
                        <td className="py-2 text-right text-slate-300">{r.sessions}</td>
                        <td className="py-2 text-right text-slate-300">${r.revenue}</td>
                        <td className="py-2 text-right font-bold text-emerald-400">${r.rpus}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Top Consumers */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-300">Top Consumers by Lifetime Revenue</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-700">
                        <th className="text-left py-2 px-2">#</th>
                        <th className="text-left py-2 px-2">Consumer</th>
                        <th className="text-center py-2 px-2">Tier</th>
                        <th className="text-right py-2 px-2">Sessions</th>
                        <th className="text-right py-2 px-2">Conversions</th>
                        <th className="text-right py-2 px-2">Revenue</th>
                        <th className="text-right py-2 px-2">RPUS</th>
                        <th className="text-left py-2 px-2">Verticals</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consumers.slice(0, 20).map((c, i) => (
                        <tr key={c.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                          <td className="py-2 px-2 text-slate-500">{i + 1}</td>
                          <td className="py-2 px-2 text-white text-xs">{c.email || c.phone || c.id.slice(0, 8)}</td>
                          <td className="py-2 px-2 text-center">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: (TIER_COLORS[c.ltv_tier] || '#64748b') + '33', color: TIER_COLORS[c.ltv_tier] || '#64748b' }}>{c.ltv_tier}</span>
                          </td>
                          <td className="py-2 px-2 text-right text-slate-300">{c.total_sessions}</td>
                          <td className="py-2 px-2 text-right text-slate-300">{c.total_conversions}</td>
                          <td className="py-2 px-2 text-right font-medium text-white">${Number(c.total_revenue || 0).toFixed(2)}</td>
                          <td className="py-2 px-2 text-right font-bold text-emerald-400">${Number(c.rpus || 0).toFixed(2)}</td>
                          <td className="py-2 px-2 text-xs text-slate-500">{(c.converted_verticals || []).join(', ').replace(/_/g, ' ') || '—'}</td>
                        </tr>
                      ))}
                      {consumers.length === 0 && (
                        <tr><td colSpan={8} className="py-8 text-center text-slate-500">No consumer profiles yet. Profiles are created when users provide their email or phone.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </PortalLayout>
  );
}

function KpiCard({ label, value, icon, color }: { label: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <Card className="bg-slate-800/50 border-slate-700">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-slate-500">{label}</span>
          <span className={color}>{icon}</span>
        </div>
        <div className="text-xl font-bold text-white">{value}</div>
      </CardContent>
    </Card>
  );
}
